import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';

export function useQueryParameter(paramName: string, resetParams?: string[]) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get(paramName) ?? '');

  const createQueryString = useCallback(
    (name: string, value: string) => {
      const params = new URLSearchParams(searchParams.toString());
      params.set(name, value);
      if (resetParams) {
        for (const param of resetParams) {
          params.delete(param);
        }
      }

      return params.toString();
    },
    [resetParams, searchParams]
  );

  const clearQueryString = useCallback(() => {
    const params = new URLSearchParams(searchParams.toString());
    params.delete(paramName);
    return params.toString();
  }, [searchParams, paramName]);

  const submitSearch = useCallback(() => {
    if (!searchTerm) {
      return router.push(pathname);
    }
    router.push(`${pathname}?${createQueryString(paramName, searchTerm)}`);
  }, [searchTerm, router, pathname, createQueryString, paramName]);

  const clearSearch = () => {
    setSearchTerm('');
    router.push(`${pathname}?${clearQueryString()}`);
  };

  useEffect(() => {
    setSearchTerm(searchParams.get(paramName) ?? '');
  }, [setSearchTerm, pathname, searchParams, paramName]);

  return { searchTerm, setSearchTerm, submitSearch, clearSearch };
}
